import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import YearSelector from '../../../Common/Selectors/YearSelector';
import PeriodSelector from '../../../Common/Selectors/PeriodSelector';
import { creators as ExpensesActions } from '../../../Reducers/Reporting/Expenses';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { financialMonth } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import ByChart from './ByChart';
import TrendChart from './TrendChart';
import BottomChart from './BottomChart';

class Expenses extends Component {
  constructor(props) {
    super(props);
    this._isMounted = true;
    this.debounceTimeout = null;
    this.lastDispatched = {
      company: null,
      dimDate: null,
    };
    const currentMonth = financialMonth(
      this.props.defaultMonth,
      this.props.defaultStartMonth,
    ) - 1;

    this.state = {
      resize: false,
      label: this.props.defaultYear.toString(),
      period: 'month',
      selectedYears: [this.props.defaultYear],
      selectedMonths: [currentMonth],
      selectedTopItems: [{ year: this.props.defaultYear, month: currentMonth }],
      selectedMiddleItems: [],
      loading: false,
    };
  }

  onResize() {
    if (this._isMounted === true) this.setState({ resize: !this.state.resize });
  }

  dispatchExpensesRequests = async () => {
    const { defaultYear, selectedCompany, defaultDimDate } = this.props;
    await this.props.getExpensesSummary([defaultYear]);
    await this.props.getExpensesDetail([defaultYear]);
    this.lastDispatched = {
      company: selectedCompany,
      dimDate: defaultDimDate,
    };
  };

  getDefaultSelection = (period = this.state.period, selectedYears = this.state.selectedYears) => {
    const { defaultYear, defaultMonth, defaultStartMonth } = this.props;
    const selectedYear = selectedYears?.[0] || defaultYear;
    const currentMonth = financialMonth(defaultMonth, defaultStartMonth) - 1;

    if (period === 'month') {
      return {
        selectedMonths: [currentMonth],
        selectedTopItems: [{ year: selectedYear, month: currentMonth }],
        selectedMiddleItems: [],
      };
    }

    return {
      selectedMonths: [],
      selectedTopItems: [],
      selectedMiddleItems: [],
    };
  };

  async componentDidMount() {
    this._isMounted = true;
    window.addEventListener('resize', this.onResize.bind(this));

    const { defaultYear } = this.props;
    const rawSetting = this.props.expensesSetting;
    const settingPeriod = 'month';

    const defaultSelection = this.getDefaultSelection(settingPeriod, [defaultYear]);

    this.setState({
      label: defaultYear.toString(),
      selectedYears: [defaultYear],
      selectedMonths: defaultSelection.selectedMonths,
      selectedTopItems: defaultSelection.selectedTopItems,
      selectedMiddleItems: defaultSelection.selectedMiddleItems,
      period: settingPeriod,
    });

    appdata.setLocalItem('expenseMonths', defaultSelection.selectedMonths);
    appdata.setLocalItem('expenseTopItems', defaultSelection.selectedTopItems);
    appdata.setLocalItem('expenseMiddleItems', defaultSelection.selectedMiddleItems);

    // If no setting existed in store, persist our default
    if (!rawSetting) {
      await this.props.reportSettingUpdateRequest({
        Expenses: JSON.stringify({ period: 'month' }),
      });
    }

    if (!this.props.lastFetchedCompany || !this.props.lastFetchedDimDate) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }
      await this.dispatchExpensesRequests();
    }
  }

  async componentDidUpdate(prevProps) {
    if (
      prevProps.defaultDimDate !== this.props.defaultDimDate ||
      prevProps.selectedCompany !== this.props.selectedCompany
    ) {
      if (this._isMounted) {
        const { defaultYear, selectedCompany, defaultDimDate, lastFetchedCompany, lastFetchedDimDate } = this.props;
        const nextPeriod = 'month';

        const defaultSelection = this.getDefaultSelection(nextPeriod, [defaultYear]);

        this.setState({
          label: defaultYear.toString(),
          selectedYears: [defaultYear],
          selectedMonths: defaultSelection.selectedMonths,
          selectedTopItems: defaultSelection.selectedTopItems,
          selectedMiddleItems: defaultSelection.selectedMiddleItems,
          period: nextPeriod,
        });

        appdata.setLocalItem('expenseMonths', defaultSelection.selectedMonths);
        appdata.setLocalItem('expenseTopItems', defaultSelection.selectedTopItems);
        appdata.setLocalItem('expenseMiddleItems', defaultSelection.selectedMiddleItems);

        if (!lastFetchedCompany || !lastFetchedDimDate) {
          if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = null;
          }
          await this.dispatchExpensesRequests();
          return;
        }

        const valuesChanged =
          lastFetchedCompany !== selectedCompany ||
          lastFetchedDimDate !== defaultDimDate;
        const alreadyDispatched =
          this.lastDispatched.company === selectedCompany &&
          this.lastDispatched.dimDate === defaultDimDate;

        if (valuesChanged && !alreadyDispatched) {
          if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
          }

          // Optimistically mark this combination as dispatched to prevent duplicate scheduling
          this.lastDispatched = {
            company: selectedCompany,
            dimDate: defaultDimDate,
          };

          this.debounceTimeout = setTimeout(() => {
            this.dispatchExpensesRequests();
            this.debounceTimeout = null;
          }, 1200);
        }
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('resize', this.onResize.bind(this));
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
  }

  handleYear = async (event) => {
    if (!event?.selectedYears?.length) {
      toast.warning('Please select the year.');
      return;
    }

    await this.props.getExpensesSummary(event.selectedYears);
    await this.props.getExpensesDetail(event.selectedYears);

    this.handleFilter(event);
  };

  handleFilter = async (event = {}) => {
    const nextPeriod = event.period || this.state.period;
    const nextSelectedYears = event.selectedYears || this.state.selectedYears;
    const periodOrYearChanged =
      (event.period !== undefined && this.state.period !== event.period) ||
      !!event.selectedYears;

    let nextEvent = { ...event };

    if (periodOrYearChanged) {
      nextEvent = {
        ...nextEvent,
        ...this.getDefaultSelection(nextPeriod, nextSelectedYears),
      };
    } else if (nextPeriod === 'month') {
      const nextMonths =
        event.selectedMonths !== undefined
          ? event.selectedMonths
          : this.state.selectedMonths;
      const nextTopItems =
        event.selectedTopItems !== undefined
          ? event.selectedTopItems
          : this.state.selectedTopItems;

      if (!nextMonths?.length || !nextTopItems?.length) {
        nextEvent = {
          ...nextEvent,
          ...this.getDefaultSelection('month', nextSelectedYears),
        };
      }
    }

    this.setState(nextEvent);

    if (nextEvent.selectedMonths) {
      appdata.setLocalItem('expenseMonths', nextEvent.selectedMonths);
    }
    if (nextEvent.selectedTopItems) {
      appdata.setLocalItem('expenseTopItems', nextEvent.selectedTopItems);
    }
    if (nextEvent.selectedMiddleItems) {
      appdata.setLocalItem('expenseMiddleItems', nextEvent.selectedMiddleItems);
    }

    await this.props.reportSettingUpdateRequest({
      Expenses: JSON.stringify({
        period: nextPeriod,
      }),
    });
  };

  render() {
    const {
      summaryData,
      detailData,
      expensesSummary,
      defaultStartMonth,
      defaultMonth,
      bubbleFlag,
    } = this.props;
    const {
      label,
      period,
      selectedYears,
      selectedMonths,
      selectedTopItems,
      selectedMiddleItems,
    } = this.state;

    const safeDetail = Array.isArray(detailData) ? detailData : [];

    let accountSubTypeLengths = [],
      maxAccountSubTypeLength = 0,
      accountNumberLengths = [],
      maxAccountNumberLengths = 0,
      accountNameLengths = [],
      maxAccountNameLengths = 0;

    safeDetail.forEach((data) => {
      if (data?.AccountSubType) accountSubTypeLengths.push(data.AccountSubType.length);
      if (data?.AccountNumber) accountNumberLengths.push(data.AccountNumber.length);
      if (data?.AccountName) accountNameLengths.push(data.AccountName.length);
    });

    if (accountSubTypeLengths.length > 0) {
      maxAccountSubTypeLength = Math.max(...accountSubTypeLengths);
      maxAccountNumberLengths = Math.max(...accountNumberLengths);
      maxAccountNameLengths = Math.max(...accountNameLengths);
    }

    return (
      <div style={{ height: '89vh' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', height: 32 }}
          id="expense_year_selector"
        >
          <PeriodSelector period={period} onChange={this.handleFilter} />
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={this.handleYear}
          />
          <div className="right well"></div>
        </div>

        <div>
          <TopChart
            summaryData={summaryData}
            period={period}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            selectedTopItems={selectedTopItems}
            defaultStartMonth={defaultStartMonth}
            defaultMonth={defaultMonth}
            handleFilter={this.handleFilter}
          />
          <ByChart
            summaryData={summaryData}
            period={period}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            selectedTopItems={selectedTopItems}
            selectedMiddleItems={selectedMiddleItems}
            defaultStartMonth={defaultStartMonth}
            defaultMonth={defaultMonth}
            handleFilter={this.handleFilter}
          />
          <TrendChart
            summaryData={summaryData}
            detailData={safeDetail}
            period={period}
            selectedYears={selectedYears}
            selectedTopItems={selectedTopItems}
            selectedMiddleItems={selectedMiddleItems}
            defaultStartMonth={defaultStartMonth}
            defaultMonth={defaultMonth}
            handleFilter={this.handleFilter}
          />
          <BottomChart
            detailData={safeDetail}
            period={period}
            selectedYears={selectedYears}
            selectedTopItems={selectedTopItems}
            selectedMiddleItems={selectedMiddleItems}
            maxAccountSubTypeLength={maxAccountSubTypeLength}
            maxAccountNumberLengths={maxAccountNumberLengths}
            maxAccountNameLengths={maxAccountNameLengths}
            handleFilter={this.handleFilter}
          />
        </div>

        {bubbleFlag && !!expensesSummary && (
          <div
            className="reporting-tooltip"
            style={{
              position: 'fixed',
              bottom: 60,
              right: 30,
              zIndex: 1000,
              opacity: 0.5,
              transition: 'opacity 0.3s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
          >
            <Tooltip
              title={expensesSummary}
              placement="top-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#2c3e50',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '500',
                    maxWidth: '400px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              <InsertCommentIcon color="success" />
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}

Expenses.propTypes = {
  // Data props (now tolerant)
  summaryData: PropTypes.object,
  detailData: PropTypes.array,
  expensesSummary: PropTypes.string,

  // Inputs/controls
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.any,

  // Optional setting – can be an object or a JSON string
  expensesSetting: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  bubbleFlag: PropTypes.bool,
};

Expenses.defaultProps = {
  summaryData: {},
  detailData: [],
  expensesSummary: '',
  expensesSetting: null,
  bubbleFlag: false,
};

const mapStateToProps = (state) => {
  return {
    summaryData: state.Reporting.Expenses.summaryData,
    detailData: state.Reporting.Expenses.detailData,
    expensesSummary: state.Reporting.Summary.reportingSummaries?.expenses ?? '',
    lastFetchedCompany: state.Reporting.Default?.defaultCompany ?? null,
    lastFetchedDimDate: state.Reporting.Default?.defaultDimDate ?? null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getExpensesSummary: (selectedYears) =>
      dispatch(ExpensesActions.expensesSummaryRequest(selectedYears)),
    getExpensesDetail: (selectedYears) =>
      dispatch(ExpensesActions.expensesDetailRequest(selectedYears)),
    reportSettingUpdateRequest: (update_setting) =>
      dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Expenses);
